import { useEffect, useState, useRef } from "react";
import PageActions from "../../utils/page/library";

export const useResponsiveBehaviour = (items, path, config = {}) => {
    const collapseOnMobile = config.collapseOnMobile ?? true;
    const usingRightSideNav = config.usingRightSideNav ?? false;
    const [hiddenItemsCount, setHiddenItemsCount] = useState(0);
    const componentInfo = useRef({
        domNode: null,
        callback: updateVisibleItems,
        isInDOM: false,
        windowWidth: 0,
        timeoutID: null,
        wallpaperWidth: 0,
    });

    useEffect(() => {
        // PageActions will update the navigation onResize
        // items that aren't in viewport will be under the "More" menu
        componentInfo.current.isInDOM = true;
        if (componentInfo.current.domNode) {
            PageActions.registerResizeObject(componentInfo.current);
        }

        return () => {
            if (componentInfo.current) {
                componentInfo.current.isInDOM = false;
                PageActions.unregisterResizeObject(componentInfo.current);
                window.cancelAnimationFrame(componentInfo.current.timeoutID);
            }
        };
    }, []);

    useEffect(() => {
        updateVisibleItems(0, { usingRightSideNav });
    }, [path, items]);

    useEffect(() => {
        componentInfo.current.callback = () =>
            updateVisibleItems(0, { usingRightSideNav });
    }, [hiddenItemsCount]);

    /**
     * Store the DOM node reference
     * @param {object} ref The React reference to the DOM node
     * @returns {void}
     */
    const assignRef = (ref) => {
        componentInfo.current.domNode = ref;
    };

    /**
     * Re-render the navigation in case not all items can fit in view
     * @param {int} windowWidth The width of the viewport, passed on from PageAction onResize handler
     * @param {int} usingRightSideNav is the right-side nav being used (for overriding purposes)
     * @returns {void}
     */
    function updateVisibleItems(
        windowWidth = 0,
        { usingRightSideNav = false } = {}
    ) {
        // if this componentInfo.wallpaperWidth is set
        // set the windowWidth to 1024 or window.innerWidth
        if (componentInfo.current.wallpaperWidth > 0) {
            windowWidth = componentInfo.current.wallpaperWidth;
        } else {
            // not using window.innerWidth since mobile has inconsistent values
            windowWidth = document.documentElement.clientWidth;
        }

        // Wait until the next browser repaint is scheduled to do the DOM calculations
        componentInfo.current.timeoutID = window.requestAnimationFrame(() => {
            const domNode = componentInfo.current.domNode;

            if (!domNode) {
                return;
            }
            if (!componentInfo.current.isInDOM) {
                return;
            }
            // Ensure that the width of the node has been defined in the DOM
            if (domNode.offsetWidth) {
                let rightNavOffsetWidth = 0;

                if (usingRightSideNav) {
                    rightNavOffsetWidth =
                        domNode.children[domNode.children.length - 1]
                            .offsetWidth;
                }

                // need to pass windowWidth to calculateHiddenItems
                // windowWidth will either be the overrideWidth or window.innerWidth
                const newHiddenItems = PageActions.calculateHiddenItems(
                    domNode,
                    collapseOnMobile,
                    windowWidth,
                    rightNavOffsetWidth
                );

                // Re-render with the hidden items
                if (hiddenItemsCount !== newHiddenItems) {
                    setHiddenItemsCount(newHiddenItems);
                }

                componentInfo.current.windowWidth = windowWidth;
            }
        });
    }

    return {
        assignRef,
        getHiddenItemsCount: () => hiddenItemsCount,
    };
};
